/**
 * HaulerCentral Auth Hook
 *
 * React hook for accessing HaulerCentral authentication state.
 * Following patterns from @commander/auth.
 */

import { useCallback, useEffect } from 'react';
import { AUTH_STORAGE_KEYS } from '../constants/authConstants';
import * as tokenProvider from '../lib/tokenProvider';
import { useHaulerCentralAuthStore } from '../stores/authStore';
import type { AuthStore, HaulerCentralUser } from '../types/auth.types';

export interface UseHaulerCentralAuthReturn extends AuthStore {
  /**
   * Check if user has a specific role/permission
   */
  hasRole: (role: string) => boolean;
  /**
   * Get user's display name
   */
  displayName: string;
  /**
   * Get user's initials for avatar
   */
  initials: string;
}

/**
 * Hook for accessing HaulerCentral auth state
 * Can be used with or without AuthProvider
 */
export function useHaulerCentralAuth(): UseHaulerCentralAuthReturn {
  const store = useHaulerCentralAuthStore();

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = () => {
      // Check if we have tokens in localStorage
      const token = tokenProvider.getAccessToken();
      const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr) as HaulerCentralUser;
          // Only update store if not already authenticated
          if (!store.isAuthenticated) {
            useHaulerCentralAuthStore.setState({
              user,
              isAuthenticated: true,
            });
          }
        } catch (error) {
          console.error('Failed to parse stored user:', error);
        }
      }
    };

    initAuth();
  }, [store.isAuthenticated]);

  // Listen for storage changes (logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEYS.ACCESS_TOKEN && e.newValue === null) {
        // Token removed - logout
        useHaulerCentralAuthStore.setState({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for auth events
  useEffect(() => {
    const handleLogin = (e: Event) => {
      const detail = (e as CustomEvent).detail as { user?: HaulerCentralUser };
      if (detail.user) {
        useHaulerCentralAuthStore.setState({
          user: detail.user,
          isAuthenticated: true,
        });
      }
    };

    const handleLogout = () => {
      useHaulerCentralAuthStore.setState({
        user: null,
        isAuthenticated: false,
        error: null,
      });
    };

    window.addEventListener('auth:login', handleLogin);
    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:login', handleLogin);
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Helper functions
  const hasRole = useCallback((_role: string) => {
    // HaulerCentral doesn't have roles, but we can add this for future
    return false;
  }, []);

  const displayName = useCallback(() => {
    const { user } = store;
    if (!user) return '';

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    return user.email;
  }, [store.user, store]);

  const initials = useCallback(() => {
    const { user } = store;
    if (!user) return '';

    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }

    return user.email.slice(0, 2).toUpperCase();
  }, [store.user, store]);

  return {
    ...store,
    hasRole,
    displayName: displayName(),
    initials: initials(),
  };
}

export default useHaulerCentralAuth;
